import { useUnifiedAddImages } from '@hakimo-ui/hakimo/data-access';
import {
  EvidenceUploaderOption,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { toast } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { useRef, useState } from 'react';
import { useAlarmGroupStatic } from '../../unified-alarm-group/alarm-provider/AlarmGroupProvider';
import { EditableTimestampHandle } from '../../shared/EditableTimestamp';
import { useFileHandler, usePasteImages } from '../util';
import { CommentBox } from './CommentBox';
import { PicturePicker } from './PicturePicker';
import { TimestampField } from './TimestampField';
import { badge, renderFields } from './util';

export function EvidenceFields({
  locationAlarmId,
  product,
  timezone,
  onUploadSuccess,
  dataVersion: dataVersionProp,
}: EvidenceUploaderOption) {
  const isScan = product === UnifiedMonitoringProduct.SCAN;
  const { dataVersion: contextDataVersion } = useAlarmGroupStatic();
  const dataVersion = dataVersionProp ?? contextDataVersion;
  const [notes, setNotes] = useState('');
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  const [timestampTouched, setTimestampTouched] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const timestampRef = useRef<EditableTimestampHandle>(null);
  const { fileList, previewList, onFilesSelect, onFileRemove, setFiles } =
    useFileHandler({ multiple: true });

  usePasteImages(setFiles);

  const { mutate, isLoading } = useUnifiedAddImages(
    () => {
      toast('Successfully added!', { type: 'success' });
      onUploadSuccess?.();
    },
    () => toast('Failed to upload!', { type: 'error' }),
    isScan ? undefined : dataVersion
  );

  return (
    <div className="flex flex-col gap-2.5">
      {renderFields([
        (n) => (
          <PicturePicker
            badge={badge(n, previewList.length > 0)}
            previewList={previewList}
            fileInputRef={fileInputRef}
            previewIdx={previewIdx}
            setPreviewIdx={setPreviewIdx}
            onFilesSelect={onFilesSelect}
            onFileRemove={onFileRemove}
          />
        ),
        (n) => (
          <TimestampField
            badge={badge(n, timestampTouched)}
            timezone={timezone}
            timestampRef={timestampRef}
            onTouched={() => setTimestampTouched(true)}
          />
        ),
        (n) => (
          <CommentBox
            badge={badge(n, notes.trim().length > 0)}
            value={notes}
            onChange={setNotes}
          />
        ),
      ])}
      <Button
        variant="primary"
        block
        onClick={() =>
          mutate(
            isScan
              ? {
                  productType: 'scan',
                  alarmGroupId: locationAlarmId,
                  comment: notes,
                  images: fileList,
                  timestamp: timestampRef.current?.getTimestamp(),
                }
              : {
                  productType: 'remote-guarding',
                  location_alarm_id: locationAlarmId,
                  comment: notes,
                  images: fileList,
                  timestamp: timestampRef.current?.getTimestamp(),
                }
          )
        }
        loading={isLoading}
        disabled={isLoading || fileList.length === 0}
      >
        Submit
      </Button>
    </div>
  );
}
